import * as React from 'react';

import { type CalendarDate, type Time } from '@internationalized/date';
import { Button, Col, Row, SmartDatePicker } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronDown, LucideUndo2 } from 'lucide-react';

import { useTranslations } from '../../i18n/useTranslations';
import type { PendingVariableChange } from '../../types/pricing';
import { BufferedTimeField } from '../_shared/BufferedTimeField';

import { $sidebarWidth } from '~/app/state/sidebar.state';

const valueFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const formatValue = (val: number | null) => {
  if (val == null) return '—';
  return valueFormatter.format(val);
};

type VariableSaveToolbarProps = {
  pendingCount: number;
  pendingChanges: Map<string, PendingVariableChange>;
  effectiveDate: CalendarDate;
  effectiveTime: Time | null;
  onDateChange: (date: CalendarDate) => void;
  onTimeChange: (time: Time | null) => void;
  onActivate: () => void;
  onRevertAll: () => void;
  onRevertChange: (variableId: string) => void;
};

type CollapsedToolbarProps = {
  pendingCount: number;
  isRevertConfirmOpen: boolean;
  setIsRevertConfirmOpen: (open: boolean) => void;
  onRevertAll: () => void;
  onExpand: () => void;
};

//
// ⚛️ ⚛️ ⚛️ COLLAPSED TOOLBAR ⚛️ ⚛️ ⚛️
//
const CollapsedToolbar = ({
  pendingCount,
  isRevertConfirmOpen,
  setIsRevertConfirmOpen,
  onRevertAll,
  onExpand,
}: CollapsedToolbarProps) => {
  const t = useTranslations();

  if (isRevertConfirmOpen) {
    return (
      <Row
        gap1
        className="@CollapsedToolbar pointer-events-auto rounded bg-neutral px-1 py-1 text-sm text-white shadow-lg"
      >
        <span className="mx-2 rounded bg-warning px-2 text-base text-white">
          {pendingCount}
        </span>
        <span className="flex-1 text-white/80">
          {t.pricing.variableSaveToolbar.confirmRevert.description}
        </span>
        <Row gap1>
          <Button
            variant="outline"
            color="neutral"
            onPress={() => setIsRevertConfirmOpen(false)}
          >
            {t.pricing.variableSaveToolbar.confirmRevert.keep}
          </Button>
          <Button
            variant="solid"
            color="failure"
            onPress={() => {
              setIsRevertConfirmOpen(false);
              onRevertAll();
            }}
          >
            {t.pricing.variableSaveToolbar.confirmRevert.confirm}
          </Button>
        </Row>
      </Row>
    );
  }

  return (
    <Row
      gap1
      className="@CollapsedToolbar pointer-events-auto rounded bg-neutral px-1 py-1 text-sm text-white shadow-lg"
    >
      <span className="mx-2 rounded bg-warning px-2 text-base text-white">
        {pendingCount}
      </span>
      <span className="flex-1 text-white/80">
        {t.pricing.variableSaveToolbar.reviewMessage}
      </span>
      <Button variant="solid" color="primary" onPress={onExpand}>
        {t.pricing.variableSaveToolbar.reviewAndSave}
      </Button>
    </Row>
  );
};

type ExpandedToolbarProps = {
  pendingCount: number;
  pendingChanges: Map<string, PendingVariableChange>;
  effectiveDate: CalendarDate;
  effectiveTime: Time | null;
  onDateChange: (date: CalendarDate) => void;
  onTimeChange: (time: Time | null) => void;
  onActivate: () => void;
  onRevertChange: (variableId: string) => void;
  onCollapse: () => void;
  onRevert: () => void;
};

//
// ⚛️ ⚛️ ⚛️ EXPANDED TOOLBAR ⚛️ ⚛️ ⚛️
//
const ExpandedToolbar = ({
  pendingCount,
  pendingChanges,
  effectiveDate,
  effectiveTime,
  onDateChange,
  onTimeChange,
  onActivate,
  onRevertChange,
  onCollapse,
  onRevert,
}: ExpandedToolbarProps) => {
  const t = useTranslations();

  const changeEntries = React.useMemo(
    () => Array.from(pendingChanges.values()),
    [pendingChanges],
  );

  return (
    <div className="@ExpandedToolbar pointer-events-auto w-full max-w-4xl rounded-lg bg-white shadow-lg">
      <Row
        gap1
        className="rounded-t-lg bg-neutral px-3 py-2.5 text-sm text-white"
      >
        <span className="mx-1 rounded bg-warning px-2 text-base text-white">
          {pendingCount}
        </span>
        <span className="flex-1 text-white/80">
          {t.pricing.variableSaveToolbar.reviewMessage}
        </span>
        <button
          className="flex size-6 items-center justify-center rounded text-white/70 hover:text-white"
          onClick={onCollapse}
        >
          <LucideChevronDown className="size-4" />
        </button>
      </Row>

      {changeEntries.length > 0 && (
        <div className="max-h-[200px] overflow-auto bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-neutral/20 bg-neutral/5">
                <th className="p-2 text-left font-semibold">
                  {t.pricing.variableSaveToolbar.review.variable}
                </th>
                <th className="p-2 text-right font-semibold">
                  {t.pricing.variableSaveToolbar.review.oldValue}
                </th>
                <th className="p-2 text-right font-semibold">
                  {t.pricing.variableSaveToolbar.review.newValue}
                </th>
                <th className="w-10 p-2" />
              </tr>
            </thead>
            <tbody>
              {changeEntries.map((change) => (
                <tr
                  key={change.variableId}
                  className="border-b border-neutral/10"
                >
                  <td className="p-2 font-medium">{change.variableName}</td>
                  <td className="p-2 text-right text-neutral/60">
                    {formatValue(change.oldValue)}
                  </td>
                  <td className="p-2 text-right">
                    <ChangeCell
                      oldValue={change.oldValue}
                      newValue={change.newValue}
                    />
                  </td>
                  <td className="p-2">
                    <button
                      className="flex size-6 items-center justify-center rounded text-neutral/60 hover:bg-neutral/10"
                      onClick={() => onRevertChange(change.variableId)}
                    >
                      <LucideUndo2 className="size-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Row
        gap3
        className="rounded-b-lg border-t border-neutral/20 bg-white px-4 py-2"
      >
        <SmartDatePicker
          value={effectiveDate}
          onChange={(date) => {
            if (date) onDateChange(date);
          }}
          className="w-40"
        />
        {effectiveTime != null && (
          <BufferedTimeField
            aria-label={t.pricing.variableSaveToolbar.effectiveTime}
            value={effectiveTime}
            onChange={onTimeChange}
          />
        )}
        <div className="flex-1" />
        <Button variant="solid" color="primary" onPress={onActivate}>
          {t.pricing.variableSaveToolbar.activate}
        </Button>
        <Button variant="outline" color="neutral" onPress={onRevert}>
          {t.pricing.variableSaveToolbar.revert}
        </Button>
      </Row>
    </div>
  );
};

type ChangeCellProps = {
  oldValue: number | null;
  newValue: number | null;
};

//
// ⚛️ ⚛️ ⚛️ CHANGE CELL ⚛️ ⚛️ ⚛️
//
const ChangeCell = ({ oldValue, newValue }: ChangeCellProps) => {
  const newDisplay = formatValue(newValue);
  const changed = oldValue !== newValue;

  if (!changed) {
    return <span>{newDisplay}</span>;
  }

  return (
    <span className="@ChangeCell">
      {oldValue != null && (
        <>
          <span className="text-neutral/40 line-through">
            {formatValue(oldValue)}
          </span>{' '}
        </>
      )}
      <span className="font-medium">{newDisplay}</span>
    </span>
  );
};

//
// ⚛️ ⚛️ ⚛️ VARIABLE SAVE TOOLBAR ⚛️ ⚛️ ⚛️
//
export const VariableSaveToolbar = ({
  pendingCount,
  pendingChanges,
  effectiveDate,
  effectiveTime,
  onDateChange,
  onTimeChange,
  onActivate,
  onRevertAll,
  onRevertChange,
}: VariableSaveToolbarProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isRevertConfirmOpen, setIsRevertConfirmOpen] = React.useState(false);

  const sidebarWidth = useAtomValue($sidebarWidth) ?? 216;

  if (pendingCount === 0) {
    return null;
  }

  return (
    <Col
      xCenter
      gap2
      className="@VariableSaveToolbar pointer-events-none fixed right-0 bottom-8 z-50"
      style={{ left: sidebarWidth }}
    >
      {!isExpanded ? (
        <CollapsedToolbar
          pendingCount={pendingCount}
          isRevertConfirmOpen={isRevertConfirmOpen}
          setIsRevertConfirmOpen={setIsRevertConfirmOpen}
          onRevertAll={onRevertAll}
          onExpand={() => setIsExpanded(true)}
        />
      ) : (
        <ExpandedToolbar
          pendingCount={pendingCount}
          pendingChanges={pendingChanges}
          effectiveDate={effectiveDate}
          effectiveTime={effectiveTime}
          onDateChange={onDateChange}
          onTimeChange={onTimeChange}
          onActivate={onActivate}
          onRevertChange={onRevertChange}
          onCollapse={() => setIsExpanded(false)}
          onRevert={() => {
            setIsExpanded(false);
            setIsRevertConfirmOpen(true);
          }}
        />
      )}
    </Col>
  );
};
